/**
 * Gear item sprite registry — maps item defs / rarity → PNG texture keys.
 *
 * Band rules:
 * - `low`  ← zip folders 1–3 / 1-3  → Common, Uncommon, Rare
 * - `high` ← zip folders 4–5 / 4-5  → Epic, Legendary (minus reserved uniques)
 *
 * Reserved high-band icons (see gearSpriteReservedUniques.json) are kept for
 * future legendary uniques and are never assigned to ordinary loot icons.
 */

import type { ItemDef, ItemRarity, WeaponFamily } from '../../../data/equipment';
import manifestJson from './gearSpriteManifest.json';
import reservedJson from './gearSpriteReservedUniques.json';

export type GearSpritePool =
  | 'belt'
  | 'shield'
  | 'focus'
  | 'sword'
  | 'spear'
  | 'bow'
  | 'axe'
  | 'greataxe'
  | 'dagger'
  | 'mace'
  | 'warhammer'
  | 'staff'
  | 'wand'
  | 'helmet'
  | 'chest'
  | 'ring';

export type GearSpriteBand = 'low' | 'high';

export type GearSpriteRarity =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'epic'
  | 'legendary';

type ManifestShape = {
  version: number;
  pools: Partial<
    Record<GearSpritePool, Partial<Record<GearSpriteBand, string[]>>>
  >;
};

type ReservedShape = {
  version: number;
  byPool: Partial<
    Record<GearSpritePool, Array<{ url: string; score: number; file: string }>>
  >;
};

const MANIFEST = manifestJson as ManifestShape;
const RESERVED = reservedJson as ReservedShape;

const HIGH_RARITIES = new Set(['epic', 'legendary', 'mythic']);

const WEAPON_FAMILY_POOL: Partial<Record<WeaponFamily, GearSpritePool>> = {
  sword: 'sword',
  /**
   * Design: spears replace 2H swords visually. Domain `greatsword` family
   * stays for combat/permissions; icons use the spear pool.
   */
  greatsword: 'spear',
  dagger: 'dagger',
  axe: 'axe',
  greataxe: 'greataxe',
  mace: 'mace',
  warhammer: 'warhammer',
  bow: 'bow',
  staff: 'staff',
  wand: 'wand',
};

/** Stable non-crypto hash for deterministic sprite picks. */
export function stableHash(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function normalizeGearRarity(
  rarity: ItemRarity | string,
): GearSpriteRarity {
  const key = String(rarity);
  if (
    key === 'common' ||
    key === 'uncommon' ||
    key === 'rare' ||
    key === 'epic' ||
    key === 'legendary'
  ) {
    return key;
  }
  if (key === 'mythic') return 'legendary';
  return 'common';
}

/** Map item rarity → sprite band (1–3 vs 4–5). */
export function gearSpriteBandForRarity(
  rarity: ItemRarity | string,
): GearSpriteBand {
  return HIGH_RARITIES.has(String(rarity)) ? 'high' : 'low';
}

/**
 * Resolve which sprite pool an item definition uses, if any.
 * Shields / focuses take priority over weapon family.
 */
export function gearSpritePoolForDef(def: ItemDef): GearSpritePool | null {
  if (def.isShield) return 'shield';
  if (def.weaponFamily) {
    const fromFamily = WEAPON_FAMILY_POOL[def.weaponFamily];
    if (fromFamily) return fromFamily;
  }
  if (def.isMagicalFocus) return 'focus';
  if (def.allowedSlots.includes('helmet')) return 'helmet';
  if (def.allowedSlots.includes('chest')) return 'chest';
  if (def.allowedSlots.includes('belt') && def.category === 'armour') {
    return 'belt';
  }
  if (
    def.allowedSlots.includes('ring_1') ||
    def.allowedSlots.includes('ring_2')
  ) {
    return 'ring';
  }
  return null;
}

export function listGearSpriteUrls(
  pool: GearSpritePool,
  band: GearSpriteBand,
): readonly string[] {
  return MANIFEST.pools[pool]?.[band] ?? [];
}

/** Reserved unique-candidate URLs for a pool (not used by ordinary resolve). */
export function listReservedUniqueUrls(pool: GearSpritePool): readonly string[] {
  return (RESERVED.byPool[pool] ?? []).map((e) => e.url);
}

export function gearSpriteTextureKey(url: string): string {
  return `gear:${url}`;
}

export type ResolvedGearSprite = {
  pool: GearSpritePool;
  band: GearSpriteBand;
  rarity: GearSpriteRarity;
  url: string;
  textureKey: string;
};

/**
 * Deterministic icon from def + rarity (+ optional instance salt).
 * Prefer `instanceId` as salt so each owned item keeps a stable icon.
 * Ordinary loot never picks reserved unique-candidate sprites.
 */
export function resolveGearSprite(
  def: ItemDef,
  rarity: ItemRarity | string,
  salt = '',
): ResolvedGearSprite | null {
  const pool = gearSpritePoolForDef(def);
  if (!pool) return null;
  const rarityKey = normalizeGearRarity(rarity);
  const preferred = gearSpriteBandForRarity(rarityKey);
  let band = preferred;
  let urls = listGearSpriteUrls(pool, band);
  // Soft fallback: high band may be empty after unique reservation (e.g. mace).
  if (urls.length === 0 && band === 'high') {
    band = 'low';
    urls = listGearSpriteUrls(pool, 'low');
  }
  if (urls.length === 0 && band === 'low') {
    urls = listGearSpriteUrls(pool, 'high');
    band = 'high';
  }
  if (urls.length === 0) return null;
  const idx =
    stableHash(`${def.id}|${rarityKey}|${salt}|${band}`) % urls.length;
  const url = urls[idx]!;
  return {
    pool,
    band,
    rarity: rarityKey,
    url,
    textureKey: gearSpriteTextureKey(url),
  };
}

/** All unique asset URLs including reserved (for preload). */
export function allGearSpriteUrls(): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (url: string) => {
    if (seen.has(url)) return;
    seen.add(url);
    out.push(url);
  };
  const pools = MANIFEST.pools;
  for (const pool of Object.keys(pools) as GearSpritePool[]) {
    const bands = pools[pool];
    if (!bands) continue;
    for (const band of ['low', 'high'] as const) {
      for (const url of bands[band] ?? []) push(url);
    }
    for (const url of listReservedUniqueUrls(pool)) push(url);
  }
  return out;
}
